import { CheckmarkFilled, WarningFilled } from '@carbon/icons-react';
import { HorizBox, Pad, PadBox } from 'component/basics';
import { CTAButton } from 'component/button';
import {
    colorButtonSecondary,
    colorPrimaryBrandWhite,
    colorTextOnTagLight,
    colorTextPrimary,
    colorTextSecondary,
    colorTextWarning,
    colorUtilityGarnet3,
    colorUtilityHoh3,
} from 'component/color';
import { Modal } from 'component/modal';
import { Heading, Paragraph, UtilityText } from 'component/text';
import { CTAButtonWithLoadingSpinner } from 'open-modules/zdf/component/button';
import { ActivityIndicator, Dimensions, StyleSheet, View } from 'react-native';
import type { Datastore } from 'util/datastore';
import { useModuleGlobal, useModuleMap } from 'util/datastore';
import { logEventAsync } from 'util/eventlog';
import { defineModuleGlobal, defineModuleMap } from 'util/moduledata';
import type { DataReadOptions } from 'util/stdtypes';
import { computedLabel, t } from 'util/stdtypes';
import { getDeploymentConfig } from 'util/util';
export type PushNotificationPermission = {
    status: boolean;
};

export enum PushPermissionCategory {
    REPLY = 'reply',
}

// eslint-disable-next-line module-data-owner -- FIXME: use module name 'push-notification'
const moduleUserPushPermissions = defineModuleMap<PushNotificationPermission>('USER_GLOBAL', 'notifications', 'pushPermissions');
export type BrowserPushPermissionState = 'default' | 'granted' | 'denied';

/**
 *
 * @returns The VAPID key for browser push notifications
 */
export function getPublicWebPushKey(): string | undefined {
    // TODO: Read it out from a file in case of local development?
    const config = getDeploymentConfig();
    return config.publicWebPushKey ?? undefined;
}

export function usePushNotificationPermission(options?: DataReadOptions) {
    const permissionMap = useModuleMap(moduleUserPushPermissions, options);
    return permissionMap?.[PushPermissionCategory.REPLY];
}

export async function setPushNotificationPermissionAsync({ datastore, value }: { datastore: Datastore; value: boolean }) {
    datastore.setModuleObjectAsync(moduleUserPushPermissions, PushPermissionCategory.REPLY, { status: value });
}

type PermissionPopupProps = {
    handleAllow?: () => void;
    handleNotAllow?: () => void;
    permission?: BrowserPushPermissionState;
    loading?: boolean;
};

export function PermissionPopup({ handleAllow, handleNotAllow, permission, loading }: PermissionPopupProps) {
    return (
        <Modal onClose={() => {}} showTopBar={false}>
            <PermissionPopupContent handleAllow={handleAllow} handleNotAllow={handleNotAllow} loading={loading} permission={permission} />
        </Modal>
    );
}

export type PermissionPopupContentProps = PermissionPopupProps;

export function PermissionPopupContent({
    handleAllow,
    handleNotAllow,
    permission = 'default',
    loading = false,
}: PermissionPopupContentProps) {
    return (
        <View
            style={{
                backgroundColor: colorButtonSecondary,
                paddingVertical: 32,
                paddingHorizontal: 20,
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 20,
            }}
        >
            <View>
                <Heading color={colorTextPrimary} label={t('Push Notifications')} type='large'></Heading>
                <Pad size={8}></Pad>
                <Paragraph
                    color={colorTextSecondary}
                    label={t(
                        'You will receive push notifications for replies to your posts. You can deactivate them at any time in your Account.',
                    )}
                    type='large'
                />
                <Pad size={32}></Pad>
                {permission === 'denied' && (
                    <>
                        <HorizBox center gap={8}>
                            <UtilityText text={'⚠️'}></UtilityText>
                            <UtilityText
                                color={colorTextWarning}
                                label={t(
                                    'Your browser has denied permissions for this site. You have to enable them in the browser settings as well.',
                                )}
                            />
                        </HorizBox>
                        <Pad size={32}></Pad>
                    </>
                )}
                <HorizBox spread>
                    <CTAButton label={t('Not allow')} onPress={handleNotAllow} type='secondary' wide></CTAButton>
                    <Pad />
                    <CTAButtonWithLoadingSpinner
                        label={t('Allow')}
                        onPress={handleAllow}
                        processing={loading}
                        wide
                    ></CTAButtonWithLoadingSpinner>
                </HorizBox>
            </View>
        </View>
    );
}

const askForPermissionStyle = StyleSheet.create({
    container: {
        backgroundColor: colorPrimaryBrandWhite,
        padding: 32,
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
    },
    containerSuccess: {
        backgroundColor: colorUtilityHoh3,
    },
    containerFailure: {
        backgroundColor: colorUtilityGarnet3,
    },
});

export type AskForPermissionScreenProps = {
    permission?: BrowserPushPermissionState;
    loading?: boolean;
    onRequest?: () => void;
    already?: boolean;
    error?: boolean;
};
export function AskForPermissionScreen({
    permission = 'default',
    loading = false,
    onRequest,
    already = false,
    error = false,
}: AskForPermissionScreenProps) {
    const s = askForPermissionStyle;

    const { height: screenHeight } = Dimensions.get('window');

    const getHeading = () => {
        if (permission === 'denied') {
            return computedLabel('Your browser blocked permissions for this domain.\nPlease unblock them in the browser settings.');
        }
        if (error) {
            return computedLabel('Something went wrong.\nSetting up push notifications failed.');
        }
        if (permission === 'granted') {
            return already
                ? computedLabel('Notifications are already enabled.\nYou can close this window.')
                : computedLabel('Notifications are now enabled.\nYou can close this window.');
        }
        if (permission === 'default') {
            return computedLabel('Please confirm to allow browser notifications.');
        }
        return computedLabel('');
    };

    const renderIcon = () => {
        if (permission === 'denied' || error) return <WarningFilled style={iconStyle} />;
        if (permission === 'granted') return <CheckmarkFilled style={iconStyle} />;
        if (loading) return <ActivityIndicator size={32} />;
        if (permission === 'default') return <CTAButton label={t('Request permission')} onPress={onRequest} size='small' />;
        return null;
    };

    const iconStyle = { height: 32, width: 32, borderRadius: 20 };

    return (
        <View
            style={[
                s.container,
                permission === 'granted' && s.containerSuccess,
                (permission === 'denied' || error) && s.containerFailure,
                { height: screenHeight },
            ]}
        >
            <View>
                <Heading center color={colorTextOnTagLight} label={getHeading()} />
            </View>
            <PadBox vert={16}>{renderIcon()}</PadBox>
        </View>
    );
}
